/** View which allows searching and filtering the public texts */
import * as Backbone from "backbone";
import * as preact from "preact";
import * as _ from "underscore";

import * as deviceDetect from "deviceDetect";
import * as bookUtils from "shared/bookUtils";
import CustomSelectView from "views/CustomSelectView";
import MultiSelectView from "views/MultiSelectView";

interface Props {
  searchQuery: Backbone.Model;
  user: Backbone.Model;
}

interface State {
  showFilters: boolean;
  searchQuery: string;
  abbreviateOptions: boolean;
}

const abbreviateForTheseSizes = ["mediumDesktop"];

class LibraryFiltersView extends preact.Component<Props, State> {
  events = { ...Backbone.Events } as Backbone.Events;

  constructor(props: Props) {
    super(props);

    this.events.listenTo(this.props.searchQuery, "change", () => {
      this.forceUpdate();
    });

    this.events.listenTo(
      this.props.user,
      "change:publicSortBooksBy change:publicBooksFilter change:publicBooksCategories",
      () => {
        this.forceUpdate();
      }
    );

    this.state = {
      showFilters: this.getNumberOfFiltersSet() > 0,
      searchQuery: "",
      abbreviateOptions: abbreviateForTheseSizes.includes(
        deviceDetect.getSizeCategory()
      ),
    };

    window.addEventListener("resize", this.onWindowResize);
  }

  onWindowResize = () => {
    this.setState({
      abbreviateOptions: abbreviateForTheseSizes.includes(
        deviceDetect.getSizeCategory()
      ),
    });
  };

  /**
   * Snap to A1, B1, C1
   */
  difficultyFrom() {
    const rawValue = this.props.user
      .get("publicBooksFilter")
      ?.metrics_CEF?.split("-")[0];

    if (!_.includes(["A", "B", "C"], rawValue[0])) {
      return "A1";
    }

    return `${rawValue[0]}1`;
  }

  /**
   * Snap to A2, B2, C2
   */
  difficultyTo() {
    const rawValue = this.props.user
      .get("publicBooksFilter")
      ?.metrics_CEF?.split("-")[1];

    if (!_.includes(["A", "B", "C"], rawValue[0])) {
      return "C2";
    }

    return `${rawValue[0]}2`;
  }

  /**
   * Snap to "0", "1000", "10000"
   */
  lengthFrom() {
    const rawValue = this.props.user
      .get("publicBooksFilter")
      ?.metrics_words?.split("-")[0];

    switch (rawValue) {
      case "0":
      case "1000":
      case "10000":
        return rawValue;

      case "3000":
        return "1000";

      case "30000":
        return "10000";
    }

    return "0";
  }

  /**
   * Snap to "1000", "10000", "Infinity"
   */
  lengthTo() {
    const rawValue = this.props.user
      .get("publicBooksFilter")
      ?.metrics_words?.split("-")[1];

    switch (rawValue) {
      case "1000":
      case "10000":
      case "Infinity":
        return rawValue;

      case "300":
        return "1000";

      case "3000":
        return "10000";
    }

    return "Infinity";
  }

  getNumberOfFiltersSet() {
    const user = this.props.user;
    let count = 0;
    if (`${this.difficultyFrom()}-${this.difficultyTo()}` !== "A1-C2") {
      count++;
    }
    if (`${this.lengthFrom()}-${this.lengthTo()}` !== "0-Infinity") {
      count++;
    }
    if (
      user.has("publicBooksCategories") &&
      _.without(user.get("publicBooksCategories"), "All").length > 0
    ) {
      count++;
    }
    if (user.get("publicBooksFilter")?.includeAI === false) {
      count++;
    }
    return count;
  }

  resetAllFilters() {
    this.props.user.save(
      {
        publicSortBooksBy: "hotness",
        publicBooksFilter: {
          metrics_CEF: "A1-C2",
          metrics_words: "0-Infinity",
        },
        publicBooksCategories: [],
      },
      { patch: true }
    );
    this.setState({ showFilters: false });
  }

  componentWillUnmount() {
    this.events.stopListening();
    window.removeEventListener("resize", this.onWindowResize);
  }

  render() {
    const numberOfFiltersSet = this.getNumberOfFiltersSet();

    const difficultyFrom = this.difficultyFrom();
    const difficultyTo = this.difficultyTo();
    const lengthFrom = this.lengthFrom();
    const lengthTo = this.lengthTo();
    const includeAI =
      this.props.user.get("publicBooksFilter")?.includeAI ?? true;

    let categories = this.props.user.get("publicBooksCategories") ?? [];
    if (categories.length === 0) {
      categories = ["All"];
    }
    const categoryCount = _.without(categories, "All").length;

    return (
      <div class="newLibraryFilters">
        <div class="searchBar">
          <span class="searchInputContainer">
            <input
              type="text"
              placeholder="Search by title or author"
              value={this.state.searchQuery}
              onInput={(event) => {
                this.setState({ searchQuery: event.currentTarget.value });
              }}
              onKeyDown={(event) => {
                if (event.keyCode === 13) {
                  event.stopPropagation();
                  event.preventDefault();

                  // Enter key was pressed
                  // Add your code here
                  this.props.searchQuery.set(
                    "query",
                    this.state.searchQuery.trim()
                  );
                }
              }}
            ></input>
            <i class="icon-search searchIcon"></i>
            {this.state.searchQuery !== "" ? (
              <i
                class="icon-remove clearSearchButton"
                onClick={() => {
                  this.setState({ searchQuery: "" });
                  this.props.searchQuery.set("query", "");
                }}
              ></i>
            ) : null}
            {this.state.searchQuery.trim().length > 0 &&
            this.state.searchQuery.trim() !==
              this.props.searchQuery.get("query") ? (
              <button
                class="searchButton"
                onClick={() =>
                  this.props.searchQuery.set(
                    "query",
                    this.state.searchQuery.trim()
                  )
                }
              >
                <i class="icon-search"></i> &nbsp; Search
              </button>
            ) : null}
          </span>
          <CustomSelectView
            options={[
              { value: "hotness", label: "Hot" },
              { value: "totalVotes", label: "Top" },
              { value: "firstShareDate", label: "New" },
            ]}
            icon="icon-sort-by-attributes"
            selectedValue={this.props.user.get("publicSortBooksBy")}
            onChange={(value: string) => {
              this.props.user.save(
                {
                  publicSortBooksBy: value,
                },
                { patch: true }
              );
            }}
          />
          <button
            onClick={() => {
              this.setState({ showFilters: !this.state.showFilters });
            }}
            style={{ whiteSpace: "nowrap" }}
          >
            Filters
            {numberOfFiltersSet > 0 ? <> ({numberOfFiltersSet})</> : ""}{" "}
            <i
              class={
                this.state.showFilters ? "icon-angle-up" : "icon-angle-down"
              }
            ></i>
          </button>
        </div>
        {this.state.showFilters ? (
          <ul class="filters">
            <li class="filter">
              <CustomSelectView
                options={[
                  { value: "A1", label: "Beginner" },
                  { value: "B1", label: "Intermediate" },
                  { value: "C1", label: "Advanced" },
                ]}
                selectedValue={difficultyFrom}
                onChange={(newDifficultyFrom: string) => {
                  // Ensure that difficultyFrom < difficultyTo
                  const newDifficultyTo = (() => {
                    if (newDifficultyFrom === "C1") {
                      return "C2";
                    } else if (
                      newDifficultyFrom === "B1" &&
                      difficultyTo === "A2"
                    ) {
                      return "B2";
                    }
                    return difficultyTo;
                  })();
                  this.props.user.save(
                    {
                      publicBooksFilter: {
                        includeAI,
                        metrics_CEF: `${newDifficultyFrom}-${newDifficultyTo}`,
                        metrics_words: `${lengthFrom}-${lengthTo}`,
                      },
                    },
                    { patch: true }
                  );
                }}
              />{" "}
              to{" "}
              <CustomSelectView
                options={[
                  { value: "A2", label: "Beginner" },
                  { value: "B2", label: "Intermediate" },
                  { value: "C2", label: "Advanced" },
                ]}
                selectedValue={difficultyTo}
                onChange={(newDifficultyTo: string) => {
                  // Ensure that difficultyFrom < difficultyTo
                  const newDifficultyFrom = (() => {
                    if (newDifficultyTo === "A2") {
                      return "A1";
                    } else if (
                      newDifficultyTo === "B2" &&
                      difficultyFrom === "C1"
                    ) {
                      return "B1";
                    }
                    return difficultyFrom;
                  })();
                  this.props.user.save(
                    {
                      publicBooksFilter: {
                        includeAI,
                        metrics_CEF: `${newDifficultyFrom}-${newDifficultyTo}`,
                        metrics_words: `${lengthFrom}-${lengthTo}`,
                      },
                    },
                    { patch: true }
                  );
                }}
              />
            </li>
            <li class="filter">
              <CustomSelectView
                options={[
                  { value: "0", label: "0 words" },
                  { value: "1000", label: "1,000 words" },
                  { value: "10000", label: "10,000 words" },
                ]}
                selectedValue={lengthFrom}
                onChange={(newLengthFrom: string) => {
                  // Ensure that lengthFrom < lengthTo
                  const newLengthTo = (() => {
                    if (newLengthFrom === "10000") {
                      return "Infinity";
                    } else if (
                      newLengthFrom === "1000" &&
                      lengthTo === "1000"
                    ) {
                      return "10000";
                    }
                    return lengthTo;
                  })();
                  this.props.user.save(
                    {
                      publicBooksFilter: {
                        includeAI,
                        metrics_CEF: `${difficultyFrom}-${difficultyTo}`,
                        metrics_words: `${newLengthFrom}-${newLengthTo}`,
                      },
                    },
                    { patch: true }
                  );
                }}
              />{" "}
              to{" "}
              <CustomSelectView
                options={[
                  { value: "1000", label: "1,000 words" },
                  { value: "10000", label: "10,000 words" },
                  { value: "Infinity", label: "10,000+ words" },
                ]}
                selectedValue={lengthTo}
                onChange={(newLengthTo: string) => {
                  // Ensure that lengthFrom < lengthTo
                  const newLengthFrom = (() => {
                    if (newLengthTo === "1000") {
                      return "0";
                    } else if (
                      newLengthTo === "10000" &&
                      lengthFrom === "10000"
                    ) {
                      return "1000";
                    }
                    return lengthFrom;
                  })();
                  this.props.user.save(
                    {
                      publicBooksFilter: {
                        includeAI,
                        metrics_CEF: `${difficultyFrom}-${difficultyTo}`,
                        metrics_words: `${newLengthFrom}-${newLengthTo}`,
                      },
                    },
                    { patch: true }
                  );
                }}
              />
            </li>
            <li class="filter">
              <CustomSelectView
                options={[
                  {
                    value: "include",
                    label: this.state.abbreviateOptions
                      ? "Inc. AI"
                      : "Include AI",
                  },
                  {
                    value: "exclude",
                    label: this.state.abbreviateOptions
                      ? "Exc. AI"
                      : "Exclude AI",
                  },
                ]}
                selectedValue={(() => {
                  return includeAI ? "include" : "exclude";
                })()}
                onChange={(value: string) => {
                  this.props.user.save(
                    {
                      publicBooksFilter: {
                        includeAI: value === "include",
                        metrics_CEF: `${difficultyFrom}-${difficultyTo}`,
                        metrics_words: `${lengthFrom}-${lengthTo}`,
                      },
                    },
                    { patch: true }
                  );
                }}
              />
            </li>
            <li class="filter">
              <MultiSelectView
                title={`Category${
                  categoryCount > 0 ? ` (${categoryCount})` : ""
                }`}
                options={[
                  {
                    value: "All",
                    label: "All",
                  },
                ].concat(
                  bookUtils.categories.map(function (category) {
                    return {
                      value: category,
                      label:
                        category === "Conversation" ? "Dialogue" : category,
                    };
                  })
                )}
                selectedValues={categories}
                onChange={(newSelectedValues: string[]) => {
                  if (
                    // If we've just clicked "all"...
                    !_.contains(categories, "All") &&
                    _.contains(newSelectedValues, "All")
                  ) {
                    // ...remove all specific categories. We represent the "all categories"
                    // condition with a single "All" category.
                    newSelectedValues = ["All"];
                  } else if (newSelectedValues.length > 1) {
                    newSelectedValues = _.without(newSelectedValues, "All");
                  }

                  // If all categories are selected...
                  if (
                    newSelectedValues.length === bookUtils.categories.length
                  ) {
                    // ...represent this with a single "All" category.
                    newSelectedValues = ["All"];
                  }

                  this.props.user.save(
                    {
                      publicBooksCategories: newSelectedValues,
                    },
                    { patch: true }
                  );
                  this.props.user.trigger("change:publicBooksCategories");
                }}
              />
            </li>
            {/* TODO: consider adding "Read status" filter...
            
            <li class="filter">
              <CustomSelectView
                options={[
                  { value: "all", label: `Read Status (All)` },
                  { value: "completed", label: "Completed" },
                  { value: "notCompleted", label: "Not Completed" },
                  { value: "notInQueue", label: "Not In My Texts" },
                ]}
              />
            </li> */}
          </ul>
        ) : null}
      </div>
    );
  }
}

export default LibraryFiltersView;
